import {
    CircularFilterConfig,
    CircularOption,
    SubjectProps,
} from '../type/SubjectProps'
import { StationProps } from '../type/StationProps'

export const circularRadius = () => {
    const value = Number(window.env.REACT_APP_CIRCULAR_RANGE)
    return Number.isFinite(value) && value > 0 ? value : 2000
}

// All pages are needed: Strapi's default page size is smaller than a map result set.
const fetchPages = async <T>(
    resource: string,
    params: URLSearchParams,
    signal: AbortSignal
): Promise<T[]> => {
    const baseUrl = window.env.REACT_APP_CIRCULAR_CMS_URL?.replace(/\/$/, '')
    if (!baseUrl) throw new Error('Chybí REACT_APP_CIRCULAR_CMS_URL')
    const result: T[] = []
    let page = 1
    let pageCount = 1
    do {
        params.set('status', 'published')
        params.set('pagination[page]', String(page))
        params.set('pagination[pageSize]', '100')
        const response = await fetch(`${baseUrl}/api/${resource}?${params}`, {
            signal,
        })
        if (!response.ok) throw new Error(`Strapi: ${response.status}`)
        const body = await response.json()
        if (!Array.isArray(body.data) || !body.meta?.pagination)
            throw new Error('Neplatná odpověď Strapi')
        result.push(...body.data)
        pageCount = body.meta.pagination.pageCount
        page++
    } while (page <= pageCount)
    return result
}

export const fetchCircularOptions = async (signal: AbortSignal) => {
    const activityParams = new URLSearchParams({
        'fields[0]': 'name',
        'sort[0]': 'sortOrder:asc',
        'sort[1]': 'name:asc',
        'filters[isActive][$eq]': 'true',
    })
    const districtParams = new URLSearchParams({
        'fields[0]': 'name',
    })
    const [activities, districts] = await Promise.all([
        fetchPages<CircularOption>('activities', activityParams, signal),
        fetchPages<CircularOption>('districts', districtParams, signal),
    ])
    districts.sort((a, b) =>
        a.name.localeCompare(b.name, 'cs', { numeric: true })
    )
    return { activities, districts }
}

// Same spherical earth radius as Google Maps, before rounding for display.
export const circularDistance = (
    a: google.maps.LatLngLiteral,
    b: google.maps.LatLngLiteral
) => {
    const radians = Math.PI / 180
    const h =
        Math.sin(((b.lat - a.lat) * radians) / 2) ** 2 +
        Math.cos(a.lat * radians) *
            Math.cos(b.lat * radians) *
            Math.sin(((b.lng - a.lng) * radians) / 2) ** 2
    return 6378137 * 2 * Math.asin(Math.sqrt(Math.min(1, h)))
}

export const subjectsToStations = (
    subjects: SubjectProps[],
    position: google.maps.LatLngLiteral,
    radius: number
): StationProps[] => {
    return subjects
        .flatMap((subject) => {
            const { latitude, longitude } = subject
            if (
                typeof latitude !== 'number' ||
                typeof longitude !== 'number' ||
                !Number.isFinite(latitude) ||
                !Number.isFinite(longitude) ||
                Math.abs(latitude) > 90 ||
                Math.abs(longitude) > 180
            )
                return []
            const distance = circularDistance(position, {
                lat: latitude,
                lng: longitude,
            })
            if (distance > radius) return []
            return [
                {
                    geometry: {
                        coordinates: [longitude, latitude] as [number, number],
                    },
                    distance: Math.round(distance),
                    properties: {
                        id: subject.documentId,
                        customId: subject.documentId,
                        name: subject.name,
                        address: {
                            address_formatted: `${subject.street} ${
                                subject.houseNumber
                            }, ${subject.postalCode} ${
                                subject.district?.name || 'Praha'
                            }`,
                        },
                        subject,
                    },
                },
            ]
        })
        .sort((a, b) => (a.distance || 0) - (b.distance || 0))
}

export const fetchCircularSubjects = async (
    position: google.maps.LatLngLiteral,
    filters: CircularFilterConfig,
    signal: AbortSignal
) => {
    const radius = circularRadius()
    // Bounding box limits the CMS response; the exact circle is checked below.
    const latDelta = ((radius / 6378137) * 180) / Math.PI
    const lngDelta =
        (Math.asin(
            Math.min(
                1,
                Math.sin(radius / 6378137) /
                    Math.cos((position.lat * Math.PI) / 180)
            )
        ) *
            180) /
        Math.PI
    const params = new URLSearchParams({
        'filters[latitude][$gte]': String(position.lat - latDelta),
        'filters[latitude][$lte]': String(position.lat + latDelta),
        'filters[longitude][$gte]': String(position.lng - lngDelta),
        'filters[longitude][$lte]': String(position.lng + lngDelta),
        'filters[activity][isActive][$eq]': 'true',
        'sort[0]': 'documentId:asc',
    })
    populateSubjectFields(params)
    filters.activities.forEach((id, i) =>
        params.set(`filters[activity][documentId][$in][${i}]`, id)
    )
    filters.districts.forEach((id, i) =>
        params.set(`filters[district][documentId][$in][${i}]`, id)
    )
    return subjectsToStations(
        await fetchPages<SubjectProps>('subjects', params, signal),
        position,
        radius
    )
}

const populateSubjectFields = (params: URLSearchParams) => {
    const fields = [
        'name',
        'street',
        'houseNumber',
        'postalCode',
        'latitude',
        'longitude',
        'skills',
        'openingHours',
        'phone',
        'email',
        'website',
        'updatedAt',
    ]
    fields.forEach((field, i) => params.set(`fields[${i}]`, field))
    ;['district', 'activity', 'specialization'].forEach((relation) =>
        params.set(`populate[${relation}][fields][0]`, 'name')
    )
}

export const fetchCircularSubject = async (
    documentId: string,
    signal: AbortSignal
) => {
    const params = new URLSearchParams({
        'filters[documentId][$eq]': documentId,
        'filters[activity][isActive][$eq]': 'true',
    })
    populateSubjectFields(params)
    const [subject] = await fetchPages<SubjectProps>('subjects', params, signal)
    if (!subject) return undefined
    return subjectsToStations([subject], { lat: 0, lng: 0 }, Infinity)[0]
}
